import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './ViewCart.css';
import {
    FaUser, FaRegCalendarAlt,
    FaArrowLeft, FaAngleRight, FaPencilAlt
} from 'react-icons/fa';
import { getMyCart, validateCoupon, updateQuantity, getOffers } from '../../../../../utils/APIs/cartApis';
import { createCartRazorpayOrder, verifyCartPayment } from '../../../../../utils/APIs/razorpayPaymentApis';
import Loader from '../../../../../Template/Loader/Loader';
import LocationPickerMap from "../../../../PersonalizedQuotePage/LocationPikerMap/LocationPikerMap";
import { getAllEditingPlans } from '../../../../../utils/APIs/editingApis';

const getMinBookingDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const y = tomorrow.getFullYear();
    const m = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const d = String(tomorrow.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const ViewCart = () => {
    const navigate = useNavigate();
    const dateInputRef = useRef(null);
    const offersRef = useRef(null);
    const [paymentMethod, setPaymentMethod] = useState('online');
    const [cartData, setCartData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [bookingDate, setBookingDate] = useState('');
    const [bookingDateIso, setBookingDateIso] = useState('');
    const [timeSlot] = useState(() => sessionStorage.getItem('vc_timeSlot') || '');
    const [couponCode, setCouponCode] = useState('');
    const [couponApplied, setCouponApplied] = useState(false);
    const [couponLoading, setCouponLoading] = useState(false);
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [userData, setUserData] = useState({});
    const [editingPlans, setEditingPlans] = useState([]);
    const [addressRequired, setAddressRequired] = useState(false);


    // Address & Location States
    const [isAddressOpen, setIsAddressOpen] = useState(false);
    const [isAddressSelected, setIsAddressSelected] = useState(false);
    
    // Offers State
    const [offersList, setOffersList] = useState([]);
    const [showOffers, setShowOffers] = useState(false);
    const [offersLoading, setOffersLoading] = useState(false);
    
    const getInitialAddressData = () => {
        return {
            location: "",
            lat: "",
            lng: "",
            streetName: "",
            city: "",
            state: "",
            postalCode: "",
            flatOrHouseNo: ""
        };
    };
    
    const [addressData, setAddressData] = useState(getInitialAddressData);



    useEffect(() => {
        sessionStorage.setItem('vc_timeSlot', timeSlot);
    }, [timeSlot]);

    useEffect(() => {
        fetchCart();
        fetchEditingPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (offersRef.current && !offersRef.current.contains(event.target)) {
                setShowOffers(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);



    const fetchEditingPlans = async () => {
        try {
            const res = await getAllEditingPlans();
            if (res?.data?.success) {
                setEditingPlans(res.data.editingPlans || res.data.plans || res.data.plansDetails || []);
            }
        } catch (error) {
            console.error("Error fetching editing plans:", error);
        }
    };
    console.log(cartData);
    //const user = cartData?.userId || {};
    const items = cartData?.items || [];

    const handleDateChange = (e) => {
        const isoValue = e.target.value;
        if (!isoValue) {
            setBookingDateIso('');
            setBookingDate('');
            return;
        }
        if (isoValue < getMinBookingDate()) {
            toast.error("Please select a date from tomorrow onwards");
            return;
        }
        setBookingDateIso(isoValue);
        const dateObj = new Date(`${isoValue}T00:00:00`);
        const formatted = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        setBookingDate(formatted);
    };

    const handleDateInputMouseDown = (e) => {
        if (!e.currentTarget?.showPicker) return;
        try {
            e.preventDefault();
            e.currentTarget.showPicker();
        } catch (err) {
            e.currentTarget.focus();
        }
    };

    const handlePencilClick = () => {
        const input = dateInputRef.current;
        if (!input) return;
        if (input.showPicker) {
            input.showPicker();
            return;
        }
        input.focus();
        input.click();
    };

    const handleLocationSelect = (data) => {
        setAddressData({
            location: data.location || "",
            lat: data.lat || "",
            lng: data.lng || "",
            streetName: data.streetName || "",
            city: data.city || "",
            state: data.state || "",
            postalCode: data.postalCode || "",
            flatOrHouseNo: addressData.flatOrHouseNo || ""
        });
        setIsAddressSelected(true);
    };

    const handleApplyCoupon = async (codeOverride = null) => {
        const code = (typeof codeOverride === 'string') ? codeOverride : couponCode;
        if (!code) {
            toast.error("Please enter a coupon code");
            return;
        }

        try {
            setCouponLoading(true);
            const baseAmount = cartData?.totalAmount || 0;

            // Determine couponType based on items
            let couponType = "Service"; // Default
            if (items.length > 0) {
                const category = items[0].category?.toLowerCase();
                if (category === "editing") couponType = "PhotoEditing";
                else if (category === "shoot_team") couponType = "HourlyShoot";
                else if (["duration", "package", "custom"].includes(category)) couponType = "CustomizeBooking";
                else if (category === "service") couponType = "Service";
            }

            // Calculate eligible amount for coupon validation
            let eligibleAmount = 0;
            items.forEach(item => {
                const cat = item.category?.toLowerCase();
                if (couponType === "HourlyShoot" && cat === "shoot_team") {
                    eligibleAmount += item.price * (item.quantity || 1);
                } else if (couponType === "PhotoEditing" && cat === "editing") {
                    eligibleAmount += item.price * (item.quantity || 1);
                } else if (couponType === "CustomizeBooking" && ["duration", "package", "custom"].includes(cat)) {
                    eligibleAmount += item.price * (item.quantity || 1);
                } else if (couponType === "Service" && cat === "service") {
                    eligibleAmount += item.price * (item.quantity || 1);
                }
            });

            if (eligibleAmount === 0) eligibleAmount = baseAmount;

            const res = await validateCoupon({
                code: code,
                amount: eligibleAmount,
                couponType: couponType
            });

            if (res?.data?.success) {
                // Reject if they typed an offer manually
                if (typeof codeOverride !== 'string' && res.data.coupon.promoType === "Offer") {
                    toast.error("Only Promo codes can be applied here. Please use 'view offers' for offers.");
                    return;
                }

                setAppliedCoupon(res.data.coupon);
                setCouponApplied(true);
                
                if (typeof codeOverride === 'string') {
                    setCouponCode(''); // Keep input clear when offer applied
                    toast.success("Offer applied successfully!");
                } else {
                    toast.success(res.data.message || "Promo code applied successfully!");
                }
            } else {
                toast.error(res?.data?.message || "Invalid coupon code");
            }
        } catch (error) {
            console.error("Error applying coupon:", error);
            toast.error(error?.response?.data?.message || "Failed to apply coupon");
        } finally {
            setCouponLoading(false);
        }
    };

    // capital first letter

    const capitalizeFirstLetter = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    }

    const fetchCart = async () => {
        try {
            setLoading(true);
            const res = await getMyCart();
            if (res?.data?.success) {
                console.log("Rezz", res);
                setUserData(res.data.data.userId);
                setCartData(res.data.data);
                if (!res.data.data?.items || res.data.data.items.length === 0) {
                    toast.info("Your cart is empty");
                    navigate('/explore-services');
                }
            } else {
                toast.info("Your cart is empty");
                navigate('/explore-services');
            }
        } catch (error) {
            console.error("Error fetching cart:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateQuantity = async (itemId, action) => {
        try {
            // No need for full page loader, just local state if possible, but for now fetchCart works
            const res = await updateQuantity({ itemId, action });
            if (res?.data?.success) {
                // Update local cart data to reflect change immediately or refetch
                setCartData(res.data.data);

            } else {
                toast.error(res?.data?.message || "Failed to update quantity");
            }
        } catch (error) {
            console.error("Error updating quantity:", error);
            toast.error("An error occurred while updating quantity");
        }
    };

    const handleRemoveCoupon = () => {
        setCouponCode('');
        setAppliedCoupon(null);
        setCouponApplied(false);
        toast.info("Coupon removed");
    };

    const handleViewOffersClick = async () => {
        if (!showOffers && offersList.length === 0) {
            try {
                setOffersLoading(true);
                const res = await getOffers({ promoType: 'Offer', active: true });
                if (res?.data?.success) {
                    setOffersList(res.data.data);
                }
            } catch (error) {
                console.error("Error fetching offers:", error);
            } finally {
                setOffersLoading(false);
            }
        }
        setShowOffers(!showOffers);
    };

    const handleSelectOffer = (code) => {
        if (couponApplied) {
            toast.info("A coupon or offer is already applied. Please remove it first to apply another.");
            setShowOffers(false);
            return;
        }
        handleApplyCoupon(code);
        setShowOffers(false);
    };

    useEffect(() => {
        if (cartData) {
            const isAddressRequired = (cartData?.items || []).some((item) => {
                const cat = item.category?.toLowerCase();
                return cat === "shoot_team" || cat === "hourly";
            });
            setAddressRequired(isAddressRequired);
        }
    }, [cartData])
    // Recalculate totals based on discount
    const subtotal = cartData?.totalAmount || 0;

    // Calculate dynamic discount
    let currentDiscount = 0;
    if (appliedCoupon) {
        let eligibleSubtotal = 0;

        items.forEach(item => {
            const cat = item.category?.toLowerCase();
            let isEligible = false;

            if (appliedCoupon.couponType === "HourlyShoot") {
                isEligible = (cat === "shoot_team");
            } else if (appliedCoupon.couponType === "PhotoEditing") {
                isEligible = (cat === "editing");
            } else if (appliedCoupon.couponType === "Service") {
                isEligible = (cat === "service");
            } else if (appliedCoupon.couponType === "CustomizeBooking") {
                isEligible = ["duration", "package", "custom"].includes(cat);
            } else {
                isEligible = true;
            }

            if (isEligible) {
                eligibleSubtotal += item.price * (item.quantity || 1);
            }
        });

        if (eligibleSubtotal === 0) eligibleSubtotal = subtotal;

        if (appliedCoupon.discountType === "percentage") {
            currentDiscount = (eligibleSubtotal * appliedCoupon.discountValue) / 100;
            // if (appliedCoupon.maxDiscountAmount) {
            //     currentDiscount = Math.min(currentDiscount, appliedCoupon.maxDiscountAmount);
            // }
        } else if (appliedCoupon.discountType === "flat") {
            currentDiscount = Math.min(appliedCoupon.discountValue, eligibleSubtotal);
        }
    }

    const finalBaseAmount = subtotal - currentDiscount;
    const convenienceFee = Number((finalBaseAmount * 0.02).toFixed(2));
    const gstAmount = Number((finalBaseAmount * 0.18).toFixed(2));
    const finalPayable = Number(Math.floor(finalBaseAmount + convenienceFee + gstAmount));

    const handleProceedToCheckout = async () => {
        // Validation: Date is mandatory
        if (!bookingDate) {
            toast.error("Please select the date");
            return;
        }

        if (addressRequired) {
            // Validation: Address is mandatory
            if (!addressData.location || !addressData.city || !addressData.postalCode || !addressData.flatOrHouseNo) {
                toast.error("Please provide a complete address including Flat/House No.");
                setIsAddressOpen(true);
                return;
            }
        }

        if (finalPayable < 5000) {

            // if (paymentMethod !== 'online') {
            //     try {
            //         setLoading(true);
            //         const dateformat = new Date(bookingDate);
            //         const formattedDate = new Date(Date.UTC(dateformat.getFullYear(), dateformat.getMonth(), dateformat.getDate())).toISOString();
            //         const res = await createHourlyBooking({
            //             client_id: userId,
            //             photographer_id: firstItem.photographer_id || null,
            //             date: formattedDate,
            //             time: timeSlot,
            //             hours: firstItem.quantity || 1,
            //             location: addressData.location,
            //             address: addressData.location,
            //             flatOrHouseNo: addressData.flatOrHouseNo,
            //             streetName: addressData.streetName,
            //             city: addressData.city,
            //             state: addressData.state,
            //             postalCode: addressData.postalCode,
            //             lat: addressData.lat,
            //             lng: addressData.lng,
            //             totalAmount: subtotal,
            //             finalAmount: finalPayable,
            //             discountAmount: currentDiscount,
            //             couponApplied: couponApplied,
            //             couponId: appliedCoupon?._id || null,
            //             cartId: cartData?._id || null,
            //             paymentStatus: "pending",
            //             paymentMethod: "COD",
            //             standardEditingVideos: editingStats.standard,
            //             premiumEditingVideos: editingStats.premium,
            //             totalEditingVideos: editingStats.total
            //         });

            //         if (res?.data?.success) {
            //             toast.success("Booking placed successfully (COD)!");

            //             if (items.some(item => item.category?.toLowerCase() === 'editing')) {
            //                 navigate(`/upload-media?bookingId=${res.data.data._id}`);
            //             } else {
            //                 navigate('/booking-success');
            //             }
            //         }
            //     } catch (error) {
            //         console.error("COD error:", error);
            //         toast.error("Failed to place booking");
            //     } finally {
            //         setLoading(false);
            //     }
            //     return;
            // }

            // toast.info('Cash on delivery is not available for this service')

            try {
                setLoading(true);

                const res = await createCartRazorpayOrder({
                    amount: finalPayable,
                    bookingDetails: {
                        date: bookingDate,
                        time: timeSlot,
                        specialInstructions: document.querySelector('.vc-special-instructions')?.value || ""
                    }
                });

                if (res?.data?.success) {
                    const order = res.data.order;

                    const options = {
                        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_YourKeyID",
                        amount: order.amount,
                        currency: order.currency,
                        name: "Veroa Photography",
                        description: "Cart Checkout",
                        order_id: order.id,
                        handler: async function (response) {
                            try {
                                setLoading(true);
                                const verifyRes = await verifyCartPayment({
                                    razorpay_order_id: response.razorpay_order_id,
                                    razorpay_payment_id: response.razorpay_payment_id,
                                    razorpay_signature: response.razorpay_signature,
                                    couponId: appliedCoupon?._id || null,
                                    bookingDetails: {
                                        date: bookingDate,
                                        time: timeSlot,
                                        address: addressData.location,
                                        city: addressData.city,
                                        state: addressData.state,
                                        postalCode: addressData.postalCode,
                                        flatOrHouseNo: addressData.flatOrHouseNo,
                                        streetName: addressData.streetName,
                                        lat: addressData.lat,
                                        lng: addressData.lng,
                                        specialInstructions: document.querySelector('.vc-special-instructions')?.value || "",
                                        totalAmount: subtotal,
                                        finalAmount: finalPayable,
                                        discountAmount: currentDiscount,
                                        convenienceFee: convenienceFee,
                                        couponApplied: couponApplied,
                                        couponId: appliedCoupon?._id || null,
                                        eventType: cartData?.eventType || "",
                                    }
                                });

                                if (verifyRes?.data?.success) {
                                    toast.success("Booking confirmed successfully!");
                                    const createdBookings = verifyRes.data.bookings || [];
                                    const firstBooking = createdBookings[0];
                                    const targetBookingId = firstBooking?._id || "";

                                    if (items.some(item => item.category?.toLowerCase() === 'editing')) {
                                        navigate(`/upload-media?bookingId=${targetBookingId}`);
                                    } else {
                                        navigate('/booking-success');
                                    }
                                } else {
                                    toast.error("Payment verification failed!");
                                }
                            } catch (error) {
                                console.error("Verification error:", error);
                                toast.error("An error occurred during verification.");
                            } finally {
                                setLoading(false);
                            }
                        },
                        prefill: {
                            name: userData?.username,
                            email: userData?.email || "email here",
                            contact: userData?.mobileNumber
                        },
                        theme: {
                            color: "#4A3326"
                        }
                    };

                    const rzp = new window.Razorpay(options);
                    rzp.open();
                }
            } catch (error) {
                console.error("Checkout error:", error);
                toast.error("Failed to initiate checkout.");
            } finally {
                setLoading(false);
            }

        } else {
            const editingStats = items.reduce((acc, item) => {
                if (item.category?.toLowerCase() === 'editing') {
                    const matched = editingPlans.find(p =>
                        (p._id?.toString() === item.planId?.toString()) ||
                        (p._id?.toString() === item.editingPlanId?.toString())
                    );
                    if (matched) {
                        const videos = parseInt(matched.numberOfVideos) || 0;
                        const totalVideosForItem = videos * (item.quantity || 1);
                        if (item.subCategoryType === 'premium') acc.premium += totalVideosForItem;
                        else acc.standard += totalVideosForItem;
                        acc.total += totalVideosForItem;
                    }
                }
                return acc;
            }, { standard: 0, premium: 0, total: 0 });

            navigate('/cart-payment-details', {
                state: {
                    finalPayable, subtotal, currentDiscount, convenienceFee, appliedCoupon, couponApplied,
                    bookingDate, timeSlot, addressData, items, userData, cartData,
                    editingStats
                }
            });
        }


    };

    return (
        <>
            {loading && <Loader />}
            <div className="view-cart-container">


                <div className="vc-header-row" style={{ display: 'flex', alignItems: 'center', position: 'relative', justifyContent: 'center', width: '100%', marginBottom: '20px' }}>
                    <button className="vc-back-btn" onClick={() => navigate(-1)} style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)' }}>
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="vc-page-title" style={{ margin: 0, textAlign: 'center' }}>View Cart</h1>
                </div>

                <div className="vc-content-wrapper">
                    {/* Left Column */}
                    <div className="vc-left-column">

                        {/* User & Booking Details */}
                        <h2 className="vc-section-heading">USER & BOOKING DETAILS</h2>
                        <div className="vc-card vc-user-booking-card">
                            <div className="vc-ub-top-section">
                                <div className="vc-ub-user-box" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                                    <div className="vc-ub-user-header" style={{ justifyContent: 'center', marginBottom: '12px' }}>
                                        <div className="vc-user-avatar-placeholder">
                                            <FaUser className="vc-ub-user-icon" />
                                        </div>
                                        <h3>{userData?.username || 'User'}</h3>
                                    </div>
                                    <p className="vc-ub-email" style={{ textAlign: 'center' }}>Number: {userData?.mobileNumber || 'N/A'}</p>
                                </div>
                                <div
                                    className="vc-ub-info-box"
                                    style={{ cursor: 'default', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
                                >
                                    <div style={{ marginBottom: '12px' }}>
                                        <h3 style={{ margin: 0, textAlign: 'center' }}>
                                            {items.some(item => item.category?.toLowerCase() === 'editing') || cartData?.eventType === "Editing" ? "Delivery Date" : "Select the Date"}
                                        </h3>
                                    </div>
                                    <div className="vc-ub-info-row" style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: bookingDate ? '#444' : '#999' }}>
                                            <FaRegCalendarAlt className="vc-ub-icon" />
                                            <span style={{ whiteSpace: 'nowrap' }}>{bookingDate || 'Select Date'}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handlePencilClick}
                                            style={{
                                                border: 'none',
                                                background: 'transparent',
                                                padding: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer'
                                            }}
                                            aria-label="Edit booking date"
                                        >
                                            <FaPencilAlt style={{ fontSize: '12px', color: '#888' }} />
                                        </button>
                                    </div>
                                    <input
                                        ref={dateInputRef}
                                        type="date" onClick={(e) => { if(e.target.showPicker) e.target.showPicker(); }}
                                        value={bookingDateIso}
                                        onChange={handleDateChange}
                                        onMouseDown={handleDateInputMouseDown}
                                        min={getMinBookingDate()}
                                        style={{
                                            position: 'absolute',
                                            width: 1,
                                            height: 1,
                                            top: 0,
                                            left: 0,
                                            opacity: 0,
                                            pointerEvents: 'none'
                                        }}
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>

                            <div className="vc-ub-divider"></div>

                            {
                                addressRequired ? (
                                    <div className="vc-ub-bottom-section">
                                        <div className="full-width-row">
                                            <div
                                                className={`accordion-header ${isAddressOpen ? "open" : ""}`}
                                                onClick={() => setIsAddressOpen(!isAddressOpen)}
                                            >
                                                <span>Event Address & Location</span>
                                                <span className="arrow">{isAddressOpen ? "▲" : "▼"}</span>
                                            </div>

                                            <div className={`accordion-body ${isAddressOpen ? "show" : ""}`}>
                                                    <div className="inner-grid">
                                                        {/* 🗺️ MAP */}
                                                        <div className="vc-map-wrapper">
                                                            <LocationPickerMap onSelect={handleLocationSelect} isOpen={isAddressOpen} />
                                                        </div>

                                                        {/* Address Inputs */}
                                                        <div className="vc-address-inputs">
                                                            <div className="vc-input-group full-width">
                                                                <label>Full Address</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control budget-input"
                                                                    placeholder="Search or select on map"
                                                                    value={addressData.location}
                                                                    onChange={(e) => setAddressData({ ...addressData, location: e.target.value })}
                                                                    disabled={isAddressSelected}
                                                                />
                                                            </div>

                                                            <div className="vc-input-row">
                                                                <div className="vc-input-group">
                                                                    <label>Flat / House No.</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control budget-input"
                                                                        placeholder="House No."
                                                                        value={addressData.flatOrHouseNo || ""}
                                                                        onChange={(e) => setAddressData({ ...addressData, flatOrHouseNo: e.target.value })}
                                                                    />
                                                                </div>
                                                                <div className="vc-input-group">
                                                                    <label>Street</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control budget-input"
                                                                        placeholder="Street"
                                                                        value={addressData.streetName}
                                                                        readOnly
                                                                        disabled={isAddressSelected}
                                                                    />
                                                                </div>
                                                            </div>

                                                            <div className="vc-input-row">
                                                                <div className="vc-input-group">
                                                                    <label>City</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control budget-input"
                                                                        placeholder="City"
                                                                        value={addressData.city}
                                                                        readOnly
                                                                        disabled={isAddressSelected}
                                                                    />
                                                                </div>
                                                                <div className="vc-input-group">
                                                                    <label>State</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control budget-input"
                                                                        placeholder="State"
                                                                        value={addressData.state}
                                                                        readOnly
                                                                        disabled={isAddressSelected}
                                                                    />
                                                                </div>
                                                                <div className="vc-input-group">
                                                                    <label>Postal Code</label>
                                                                    <input
                                                                        type="text"
                                                                        className="form-control budget-input"
                                                                        placeholder="Zip"
                                                                        value={addressData.postalCode}
                                                                        readOnly
                                                                        disabled={isAddressSelected}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* hidden lat/lng */}
                                                        <input type="hidden" value={addressData.lat} />
                                                        <input type="hidden" value={addressData.lng} />
                                                    </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : null
                            }

                            <input
                                type="text"
                                className="vc-special-instructions"
                                placeholder="Add any special instructions (optional)"
                            />
                        </div>

                        {/* Payment Method Left */}
                        <h2 className="vc-section-heading mt-4">Payment Method</h2>
                        <div className="vc-card vc-payment-card-left">
                            <div className="vc-payment-options">
                                <label className={`vc-payment-option ${paymentMethod === 'online' ? 'active' : ''}`}>
                                    <div className="vc-payment-option-header">
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            checked={paymentMethod === 'online'}
                                            onChange={() => setPaymentMethod('online')}
                                        />
                                        <span>Online Payment</span>
                                        <FaAngleRight className="vc-payment-arrow" />
                                    </div>
                                    {paymentMethod === 'online' && (
                                        <div className="vc-payment-logos">
                                            <span className="vc-pay-type" onClick={handleProceedToCheckout}>UPI</span>
                                            <span className="vc-pay-type" onClick={handleProceedToCheckout}>Credit / Debit Cards</span>
                                            <span className="vc-pay-type" onClick={handleProceedToCheckout}>Wallets</span>
                                            <span className="vc-pay-type" onClick={handleProceedToCheckout}>EMI</span>
                                        </div>
                                    )}
                                </label>

                                {/* <label className={`vc-payment-option ${paymentMethod === 'cod' ? 'active' : ''}`}>
                                    <div className="vc-payment-option-header">
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            checked={paymentMethod === 'cod'}
                                            onChange={() => setPaymentMethod('cod')}
                                        />
                                        <span>Cash on Delivery</span>
                                    </div>
                                </label> */}
                                {/* Pay later option */}
                                {/* <label className={`vc-payment-option ${paymentMethod === 'pay_later' ? 'active' : ''}`}>
                                    <div className="vc-payment-option-header">
                                        <input
                                            type="radio"
                                            name="payment_method"
                                            checked={paymentMethod === 'pay_later'}
                                            onChange={() => setPaymentMethod('pay_later')}
                                        />
                                        <span>Pay Later</span>
                                        <FaAngleRight className="vc-payment-arrow" />
                                    </div>
                                </label> */}
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="vc-right-column">

                        {/* Cart Details */}
                        <div className="vc-card vc-cart-details-card">
                            <h3 className="vc-sidebar-title">Cart Details</h3>
                            <div className="vc-cart-items-wrapper">
                                {items.length > 0 ? items.map((item, index) => (
                                    <div className="vc-cart-item-box" key={item._id || index}>
                                        <div className="vc-cart-item-info">
                                            <div className="vc-cart-item-header">
                                                <span className="vc-cart-item-title">
                                                    {capitalizeFirstLetter(item.name)}
                                                </span>
                                            </div>
                                            <div className="vc-cart-item-pricing">
                                                <span className="vc-cart-item-duration">
                                                    {(() => {
                                                        let nameStr = capitalizeFirstLetter(item.name).replace(/\s*\(\d+\s*videos?\)/i, '');
                                                        let badgeType = '';
                                                        if (nameStr.toLowerCase().includes('standard') || item.subCategoryType === 'standard') {
                                                            badgeType = 'Standard';
                                                        } else if (nameStr.toLowerCase().includes('premium') || item.subCategoryType === 'premium') {
                                                            badgeType = 'Premium';
                                                        }

                                                        if (badgeType) {
                                                            return <><span className={`vc-plan-badge ${badgeType.toLowerCase()}`}>{badgeType}</span> | ₹{(item.price * (item.quantity || 1)).toLocaleString()}</>;
                                                        }
                                                        
                                                        let prefix = '';
                                                        if (nameStr.toLowerCase() !== 'shoot_team' && nameStr.toLowerCase() !== 'shoot team') {
                                                            prefix = `${nameStr} | `;
                                                        }
                                                        
                                                        return `${prefix}₹${(item.price * (item.quantity || 1)).toLocaleString()}`;
                                                    })()}
                                                </span>



                                                {/* {['editing'].includes(item.category?.toLowerCase()) && (
                                                {/* {['editing'].includes(item.category?.toLowerCase()) && (
                                            <div
                                                className="vc-item-upload-link"
                                                onClick={() => navigate(`/upload-media?cartId=${item._id}`)}
                                                style={{ color: '#332D2D', fontSize: '0.8rem', textDecoration: 'underline', marginTop: '5px', cursor: 'pointer' }}
                                            >
                                                {item.fileKeys?.length > 0 ? `${item.fileKeys.length} Files Uploaded` : 'Upload Files'}
                                            </div>
                                        )} */}

                                            </div>
                                        </div>
                                        <div className="vc-cart-qty">
                                            <button onClick={() => handleUpdateQuantity(item._id, 'decrease')}>-</button>
                                            <span>{item.quantity}</span>
                                            <button onClick={() => handleUpdateQuantity(item._id, 'increase')}>+</button>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="vc-cart-item-box">
                                        <p>Your cart is empty</p>
                                    </div>
                                )}

                            </div>
                            
                            {appliedCoupon?.promoType === "Offer" ? (
                                <div className="vc-coupon-box" style={{ background: '#fcfcfc', border: '1px solid #ddd', padding: '10px 14px', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '14px', fontWeight: '600', color: '#4A3326' }}>Offer Applied: {appliedCoupon.code}</span>
                                    <button className="vc-coupon-btn remove" onClick={handleRemoveCoupon} style={{ padding: '6px 14px' }}>Remove</button>
                                </div>
                            ) : (
                                <div className="vc-coupon-box">
                                    <input
                                        type="text"
                                        placeholder="Apply Promo Code"
                                        className="vc-coupon-input"
                                        value={couponCode}
                                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                        disabled={couponApplied || couponLoading}
                                    />
                                    {couponApplied ? (
                                        <button className="vc-coupon-btn remove" onClick={handleRemoveCoupon}>Remove</button>
                                    ) : (
                                        <button
                                            className="vc-coupon-btn"
                                            onClick={() => handleApplyCoupon()}
                                            disabled={couponLoading}
                                        >
                                            {couponLoading ? "..." : "Apply"}
                                        </button>
                                    )}
                                </div>
                            )}
                            
                            <div ref={offersRef} style={{ marginTop: '8px', paddingLeft: '4px', position: 'relative', width: '100%' }}>
                                <span 
                                    onClick={() => { if (!couponApplied || appliedCoupon?.promoType === "Offer") handleViewOffersClick(); }}
                                    style={{ 
                                        color: (couponApplied && appliedCoupon?.promoType !== "Offer") ? '#aaa' : '#4A3326', 
                                        cursor: (couponApplied && appliedCoupon?.promoType !== "Offer") ? 'not-allowed' : 'pointer', 
                                        fontSize: '0.85rem', fontWeight: '600', userSelect: 'none' 
                                    }}
                                >
                                    view offers {showOffers ? '<<' : '>>'}
                                </span>
                                {showOffers && (!couponApplied || appliedCoupon?.promoType === "Offer") && (
                                    <div style={{
                                        position: 'absolute', top: '100%', left: 0, 
                                        background: '#fff', border: '1px solid #e0e0e0', borderRadius: '8px', 
                                        padding: '12px', width: '100%', maxHeight: '320px', overflowY: 'auto', zIndex: 10,
                                        boxShadow: '0 8px 24px rgba(0,0,0,0.15)', marginTop: '8px', boxSizing: 'border-box'
                                    }}>
                                        {offersLoading ? (
                                            <div style={{ fontSize: '0.85rem', color: '#666', textAlign: 'center', padding: '10px' }}>Loading offers...</div>
                                        ) : offersList.length > 0 ? (
                                            offersList.map((offer, index) => (
                                                <div 
                                                    key={offer._id} 
                                                    onClick={() => handleSelectOffer(offer.code)}
                                                    style={{
                                                        padding: '12px 10px', 
                                                        borderBottom: index < offersList.length - 1 ? '1px solid #f0f0f0' : 'none', 
                                                        cursor: 'pointer',
                                                        display: 'flex', 
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        borderRadius: '6px', 
                                                        transition: 'background-color 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f5f3'}
                                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                                >
                                                    {/* Left: Description */}
                                                    <div style={{ flex: '1', textAlign: 'left', paddingRight: '10px' }}>
                                                        {offer.description && (
                                                            <span style={{ fontSize: '0.8rem', color: '#555', lineHeight: '1.3' }}>{offer.description}</span>
                                                        )}
                                                    </div>

                                                    {/* Right: Percentage above, Code below */}
                                                    <div style={{ flex: '1', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                                        {offer.discountType === 'percentage' ? (
                                                            <span style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '0.85rem' }}>{offer.discountValue}% OFF</span>
                                                        ) : (
                                                            <span style={{ color: '#27ae60', fontWeight: 'bold', fontSize: '0.85rem' }}>₹{offer.discountValue} OFF</span>
                                                        )}
                                                        <span style={{ fontWeight: '700', color: '#4A3326', fontSize: '0.85rem', border: '1px dashed #4A3326', padding: '2px 6px', borderRadius: '4px', whiteSpace: 'nowrap' }}>
                                                            {offer.code}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div style={{ fontSize: '0.85rem', color: '#666', textAlign: 'center', padding: '10px' }}>No offers available right now.</div>
                                        )}
                                    </div>
                                )}
                            </div>

                            </div>
                            
                            {/* Payment Summary */}
                            <div className="vc-card vc-payment-summary-card">
                                <h3 className="vc-sidebar-title">Payment Summary</h3>
                                <div className="vc-summary-row">
                                    <span>Subtotal</span>
                                    <span>₹{subtotal.toLocaleString()}</span>
                                </div>
                                {currentDiscount > 0 && (
                                    <div className="vc-summary-row" style={{ color: '#27ae60', fontWeight: '600' }}>
                                        <span>Discount {appliedCoupon?.discountType === 'percentage' ? `(${appliedCoupon.discountValue}%)` : ''}</span>
                                        <span>-₹{currentDiscount.toLocaleString()}</span>
                                    </div>
                                )}
                                <div className="vc-summary-row">
                                    <span>Convenience Fee (2%)</span>
                                    <span>₹{convenienceFee.toLocaleString()}</span>
                                </div>
                                <div className="vc-summary-row">
                                    <span>Taxes</span>
                                    <span>₹0</span>
                                </div>
                                <div className="vc-summary-row">
                                    <span>GST (18%)</span>
                                    <span>₹{gstAmount.toLocaleString()}</span>
                                </div>
                                <div className="vc-summary-divider"></div>
                                <div className="vc-summary-total">
                                    <span className="vc-summary-total-label">Final Payable Amount</span>
                                    <span className="vc-summary-total-price">₹{finalPayable.toLocaleString()}</span>
                                </div>
                            </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ViewCart;
